import { useCallback, useEffect, useRef, useState } from 'react'
import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'
import { DwgCleanService, isDwgFilename, type DwgCount, type DwgLayer } from '../../../../../services/DwgCleanService'
import type { FileInfo } from '../../../../../models/FileStorage'
import type { DwgCleanModalProps } from '../../DwgCleanModal'

/** 사용자가 DWG 정리 화면에서 취소했다 — 오류 화면 대신 CAD 목록으로 돌아간다. */
export class DwgCleanCancelled extends Error {
  constructor() {
    super('DWG 정리를 취소했습니다')
    this.name = 'DwgCleanCancelled'
  }
}

interface Pending {
  filename: string
  layers: DwgLayer[]
  counts: DwgCount[]
  ai: string
}

/**
 * DWG 는 파서가 못 읽으니 레이어 분석 전에 이 단계를 거친다:
 * 레이어 목록 → 체크 화면(사람이 확인) → 체크 안 한 레이어를 지운 DXF 를 CAD 저장소에 저장 → 그 DXF 를 돌려준다.
 * DXF 는 그대로 돌려준다.
 */
export function useDwgCleanStep(
  siteProjectId: number | null,
  onProgress: (message: string, progress: number) => void,
) {
  const [pending, setPending] = useState<Pending | null>(null)
  const answerRef = useRef<((deleteLayers: string[] | null) => void) | null>(null)

  // 화면이 닫히면 기다리던 단계를 취소로 끝낸다.
  useEffect(() => () => answerRef.current?.(null), [])

  const answer = useCallback((deleteLayers: string[] | null) => {
    const resolve = answerRef.current
    answerRef.current = null
    setPending(null)
    resolve?.(deleteLayers)
  }, [])

  const resolveDwg = useCallback(
    async (file: FileInfo): Promise<FileInfo> => {
      if (!isDwgFilename(file.filename)) return file
      if (!siteProjectId) throw new Error('사이트 프로젝트 ID가 없습니다.')

      onProgress('DWG 레이어 읽는 중... (AutoCAD + AI 추천, 1분 안팎)', 30)
      const { layers, counts, ai } = await DwgCleanService.listLayers(siteProjectId, file.filename)

      onProgress('남길 레이어를 확인하세요', 40)
      const deleteLayers = await new Promise<string[] | null>((resolve) => {
        answerRef.current = resolve
        setPending({ filename: file.original_name || file.filename, layers, counts, ai })
      })
      if (!deleteLayers) throw new DwgCleanCancelled()

      onProgress(`DWG 정리 중... 레이어 ${deleteLayers.length}개 삭제 (1분 안팎)`, 45)
      const result = await DwgCleanService.clean(siteProjectId, file.filename, deleteLayers)
      log.info(LogCategory.CSV, 'DWG cleaned', {
        dwg: file.filename,
        dxf: result.file.filename,
        deleted: result.deletedCount,
        failed: result.failedLayers,
      })
      if (result.failedLayers.length > 0) {
        log.warn(LogCategory.CSV, 'DWG layers not deleted', { failed: result.failedLayers })
      }
      return result.file
    },
    [siteProjectId, onProgress],
  )

  const dwgModal: DwgCleanModalProps | null = pending && {
    filename: pending.filename,
    layers: pending.layers,
    counts: pending.counts,
    ai: pending.ai,
    onConfirm: (deleteLayers) => answer(deleteLayers),
    onCancel: () => answer(null),
  }

  return { resolveDwg, dwgModal }
}
